import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { CircularProgress, RadioGroup, Typography } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';
import { PDF_EXPORT_INFO_MESSAGE } from 'common/constants/documents';
import { useStore } from 'common/store';
import { PdfExportConfig, PdfExporter, TEMPLATE_CONSTANTS } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { getAllocationStatus, Logger } from 'utilities';
import { useIsCompanyPath } from 'utilities/useIsCompanyPath/useIsCompanyPath';
import { useIsFundPath } from 'utilities/useIsFundPath/useIsFundPath';
import { CompanySubOptions } from './components/CompanySubOptions';
import { FirmSubOptions } from './components/FirmSubOptions';
import { FundSubOptions } from './components/FundSubOptions';
import { RadioOption } from './components/RadioOption';
import { usePdfPages } from './hooks/usePdfPages';
import {
  ExportProgressContainer,
  PdfExportButton,
  ProgressSubtext,
  ProgressText,
  StyledButton,
  StyledContentContainer,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  StyledInfoIcon,
  StyledRadioContainer,
} from './PdfExportConfigDialog.styles';
import {
  DEFAULT_PDF_CONFIGURATION,
  PdfExportConfigDialogProps,
  PdfExportConfiguration,
  PdfExportOption,
} from './PdfExportConfigDialog.types';

const PdfExportConfigDialog = ({ open, onClose }: PdfExportConfigDialogProps) => {
  const [configuration, setConfiguration] = useState<PdfExportConfiguration>(DEFAULT_PDF_CONFIGURATION);

  // Determine current context
  const isCompanyPath = useIsCompanyPath();
  const isFundPath = useIsFundPath();
  const isFirmPath = useMemo(() => !isCompanyPath && !isFundPath, [isCompanyPath, isFundPath]);

  let coverTitle = 'PDF Report of';

  if (isCompanyPath) {
    coverTitle = 'Valuation Report of';
  } else if (isFundPath) {
    coverTitle = 'Fund Report of';
  } else if (isFirmPath) {
    coverTitle = 'Firm Report of';
  }

  // Get entity information from store
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { allocationInfo, companyInfo, firmInfo, fundInfo, fundList, valuationInfo } = storeValues;
  const params = useParams();
  const location = useLocation();

  // Get the current entity name based on context
  const entityName = useMemo(() => {
    if (isCompanyPath && companyInfo?.name) {
      return companyInfo.name;
    }
    if (isFundPath) {
      // First check fundInfo from store
      if (fundInfo?.name) {
        return fundInfo.name;
      }
      // Otherwise find it from fundList using slug
      const fundSlug = params.fundSlugParam;
      if (fundSlug && fundList) {
        const currentFund = fundList.find(fund => fund.slug === fundSlug);
        return `${currentFund?.name}`;
      }
    }
    if (isFirmPath && firmInfo?.name) {
      return firmInfo.name;
    }
    return '';
  }, [isCompanyPath, isFundPath, isFirmPath, companyInfo, firmInfo, fundInfo, fundList, params.fundSlugParam]);

  // Get measurement date from query string
  const measurementDate = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get('date') ?? '';
    if (raw === '' || raw === 'undefined') {
      return '';
    }
    return raw;
  }, [location.search]);

  // Generate dynamic filename based on entity name and date
  const filename = useMemo(() => {
    const parts = [];

    if (entityName) {
      // Sanitize entity name for filename (remove special characters)
      const sanitizedName = entityName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(sanitizedName);
    }

    if (measurementDate) {
      parts.push(measurementDate);
    }

    return parts.length > 0 ? parts.join('-') : 'pdf-export';
  }, [entityName, measurementDate]);

  // Get dynamic URLs based on current context and configuration
  const pages = usePdfPages(configuration);

  const isExportDisabled = pages.length === 0;

  // Automatically set the selected option based on current context
  useEffect(() => {
    if (isCompanyPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.VALUATIONS }));
    } else if (isFundPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FUND }));
    } else if (isFirmPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FIRM }));
    }
  }, [isCompanyPath, isFundPath, isFirmPath]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    currentPage: 0,
    totalPages: 0,
    percentage: 0,
    isProcessing: false,
  });

  const exporter = useRef(new PdfExporter());

  const handleOptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value as PdfExportOption;
    setConfiguration(prev => {
      const newConfig = {
        ...prev,
        selectedOption,
      };

      if (selectedOption === PdfExportOption.VALUATIONS) {
        newConfig.companyOptions = {
          valuationSummary: true,
          equityAllocation: true,
          concludedShareValues: true,
          financials: true,
          fundOwnership: true,
          capTable: true,
          breakpointAnalysis: true,
          cashFlowLedger: true,
          valuationApproaches: true,
        };
      }

      return newConfig;
    });
  }, []);

  const handleCompanyOptionsChange = useCallback(
    (options: typeof configuration.companyOptions) => {
      setConfiguration(prev => ({
        ...prev,
        companyOptions: options,
      }));
    },
    [configuration]
  );

  const handleFundOptionsChange = useCallback(
    (options: typeof configuration.fundOptions) => {
      setConfiguration(prev => ({
        ...prev,
        fundOptions: options,
      }));
    },
    [configuration]
  );

  const handleFirmOptionsChange = useCallback(
    (options: typeof configuration.firmOptions) => {
      setConfiguration(prev => ({
        ...prev,
        firmOptions: options,
      }));
    },
    [configuration]
  );

  const handleExport = useCallback(async () => {
    if (firmInfo) {
      try {
        setIsExporting(true);
        setExportProgress({ currentPage: 0, totalPages: pages.length, percentage: 0, isProcessing: false });

        // General data variables
        const firmName = firmInfo.name;
        const isFirmOwned = valuationInfo?.is_firm_owned || false;
        const dateOfIssue = `As of: ${moment().format('MMMM D, YYYY')}`;

        // Image related variables
        const firmLogo = firmInfo.logo_url || TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
        const coverBackgroundImage = isFirmOwned ? TEMPLATE_CONSTANTS.COVER_LIGHT_BACKGROUND_JPG : TEMPLATE_CONSTANTS.COVER_BLUE_BACKGROUND_JPG;
        const coverLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_WHITE_LOGO_PNG;
        const coverSecondaryLogo = TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
        const pageLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
        const pageBackgroundImage = TEMPLATE_CONSTANTS.PAGES_BACKGROUND_JPG;
        const footerLogo = TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
        const infoIcon = TEMPLATE_CONSTANTS.INFO_ICON_PNG;


        // Get allocation status
        let allocationStatus: number | undefined;
        if (allocationInfo) {
          const { is_final: isFinal, is_published: isPublished } = allocationInfo;
          allocationStatus = getAllocationStatus({ isFinal: Boolean(isFinal), isPublished: Boolean(isPublished) });
        }

        const config: PdfExportConfig = {
          filename,
          entityName,
          coverTitle,
          dateOfIssue,
          pages,
          isFirmOwned,
          coverBackgroundImage,
          coverLogo,
          coverSecondaryLogo,
          pageBackgroundImage,
          pageLogo,
          footerLogo,
          infoIcon,
          firmName,
          allocationStatus,
          measurementDate,
          onProgress: (currentPage, totalPages, percentage) => {
            const calculatedPercentage = Math.round((currentPage / totalPages) * 100);
            const finalPercentage = percentage !== undefined ? Math.round(percentage) : calculatedPercentage;
            setExportProgress({ currentPage, totalPages, percentage: finalPercentage, isProcessing: true });
          },
        };
        await exporter.current.export(config);

        setIsExporting(false);
        onClose();
      } catch {
        setIsExporting(false);
        setExportProgress({ currentPage: 0, totalPages: 0, percentage: 0, isProcessing: false });
        onClose();
      }
    } else {
      Logger.error(
        'Failed to export PDF: No valuation info or firm info found',
        new Error('Failed to export PDF: No valuation info or firm info found')
      );
      enqueueSnackbar('Failed to export PDF: No valuation info or firm info found', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [
    exporter,
    onClose,
    pages,
    filename,
    entityName,
    firmInfo,
    valuationInfo,
    coverTitle,
    allocationInfo,
    measurementDate,
  ]);

  const handleDialogClose = useCallback(() => {
    if (isExporting) {
      return;
    }
    onClose();
  }, [isExporting, onClose]);

  return (
    <StyledDialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown={isExporting}>
      <StyledDialogTitle>{`${entityName} PDF Export`}</StyledDialogTitle>

      <StyledDialogContent>
        {isExporting && (
          <ExportProgressContainer>
            <CircularProgress size={60} />
            <ProgressText>{exportProgress.isProcessing ? 'Processing...' : 'Preparing export...'}</ProgressText>
            <ProgressSubtext>
              {exportProgress.isProcessing
                ? `Page ${exportProgress.currentPage} of ${exportProgress.totalPages} (${exportProgress.percentage}%)`
                : 'Setting up your PDF export...'}
            </ProgressSubtext>
          </ExportProgressContainer>
        )}

        <StyledRadioContainer>
          <RadioGroup value={configuration.selectedOption} onChange={handleOptionChange}>
            {isCompanyPath && (
              <>
                <RadioOption value={PdfExportOption.VALUATIONS} label="Valuations" />
                <CompanySubOptions
                  options={configuration.companyOptions}
                  onOptionsChange={handleCompanyOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.VALUATIONS}
                />
              </>
            )}

            {isFundPath && (
              <>
                <RadioOption value={PdfExportOption.FUND} label="Fund" />
                <FundSubOptions
                  options={configuration.fundOptions}
                  onOptionsChange={handleFundOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FUND}
                />
              </>
            )}

            {isFirmPath && (
              <>
                <RadioOption value={PdfExportOption.FIRM} label="Firm" />
                <FirmSubOptions
                  options={configuration.firmOptions}
                  onOptionsChange={handleFirmOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FIRM}
                />
              </>
            )}
          </RadioGroup>
        </StyledRadioContainer>

        <StyledContentContainer>
          <StyledInfoIcon>
            <InfoIcon color="action" />
          </StyledInfoIcon>
          <Typography>{PDF_EXPORT_INFO_MESSAGE}</Typography>
        </StyledContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose}>Cancel</StyledButton>
        <PdfExportButton
          onClick={handleExport}
          disabled={isExportDisabled || isExporting}
          isExporting={isExporting}>
          {isExporting ? 'Exporting...' : 'Export PDF'}
        </PdfExportButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default PdfExportConfigDialog;
